import React, { useEffect, useMemo, useState } from "react";
import { format, isValid as isValidDate, set as setTime } from "date-fns";
import {
  CalendarIcon,
  Clock,
  Loader2,
  CalendarPlus,
  AlertCircle,
  ExternalLink,
  CheckCircle2,
  Globe,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import usePublishEligibility from "@/lib/usePublishEligibility";
import useBillingSubscription, { publishUnitsNeeded, evaluatePublishQuota } from "@/lib/useBillingSubscription";
import QuotaBlockBanner from "@/components/QuotaBlockBanner";
import { platformLabel } from "@/components/PlatformBadges";

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTE_OPTIONS = ["00", "15", "30", "45"];
const MERIDIEM_OPTIONS = ["AM", "PM"];

function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (_e) {
    return "UTC";
  }
}

function applyTimeOfDay(baseDate, hour12, minute, meridiem) {
  // Convert 12-hour to 24-hour; defensive about all inputs being strings.
  const h = Number(hour12);
  const m = Number(minute);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  let h24 = h % 12; // 12 -> 0
  if (meridiem === "PM") h24 += 12;
  return setTime(baseDate, { hours: h24, minutes: m, seconds: 0, milliseconds: 0 });
}

/**
 * SchedulePostDialog \u2014 polished, dark, accessible scheduling modal.
 *
 * Replaces the bare `datetime-local` input that caused the
 * `RangeError: Invalid time value` crash. All Date \u2192 ISO conversions are
 * guarded by `isValid(...)` so a bad input results in a friendly inline
 * error message, never an uncaught exception.
 *
 * Props:
 *   open, onOpenChange   \u2014 controlled
 *   platform             \u2014 string; used to query publish-eligibility and
 *                          to display the target platform line.
 *   initialDate          \u2014 optional Date|string. Falls back to "now + 1h".
 *   onConfirm(isoDate)   \u2014 called with a verified ISO string in the future.
 *                          Should return a promise; the dialog stays open
 *                          until it resolves so callers can show a loader.
 *   loading              \u2014 disables the confirm button externally.
 *   testidPrefix         \u2014 default "schedule-dialog"
 */
export default function SchedulePostDialog({
  open,
  onOpenChange,
  platform,
  targetPlatforms,
  initialDate,
  onConfirm,
  loading: externalLoading = false,
  testidPrefix = "schedule-dialog",
}) {
  // Normalize the multi-platform list defensively so callers can pass
  // either ["x","LinkedIn"] or canonical display labels. Falls back to
  // [platform] so downstream rendering can be uniform.
  const normalizedTargets = useMemo(() => {
    const arr = Array.isArray(targetPlatforms) ? targetPlatforms : [];
    const seen = new Set();
    const out = [];
    for (const item of arr) {
      const label = platformLabel(item);
      if (label && !seen.has(label)) {
        seen.add(label);
        out.push(label);
      }
    }
    if (out.length === 0 && platform) out.push(platformLabel(platform));
    return out;
  }, [targetPlatforms, platform]);
  const isMulti = normalizedTargets.length > 1;

  // Eligibility check \u2014 only fired while the dialog is open so we don't
  // make a request for every closed instance on the page.
  //
  // In multi-platform mode we ALSO fire one hook per supported platform so
  // every target row can show its own connection / Meta-gate status. The
  // four extra hooks are called in a fixed order so React's rules-of-hooks
  // are satisfied; each is gated by ``enabled`` to skip the network call
  // when that platform isn't selected.
  const { eligibility, loading: eligLoading } = usePublishEligibility(platform, {
    enabled: !!open && !!platform,
  });
  const xElig = usePublishEligibility("X", {
    enabled: !!open && isMulti && normalizedTargets.includes("X"),
  });
  const liElig = usePublishEligibility("LinkedIn", {
    enabled: !!open && isMulti && normalizedTargets.includes("LinkedIn"),
  });
  const fbElig = usePublishEligibility("Facebook", {
    enabled: !!open && isMulti && normalizedTargets.includes("Facebook"),
  });
  const igElig = usePublishEligibility("Instagram", {
    enabled: !!open && isMulti && normalizedTargets.includes("Instagram"),
  });
  const eligByPlatform = useMemo(() => ({
    X: xElig,
    LinkedIn: liElig,
    Facebook: fbElig,
    Instagram: igElig,
  }), [xElig, liElig, fbElig, igElig]);

  const tz = useMemo(() => detectTimezone(), []);

  // Default to (now + 60 min) rounded to the next 15-minute slot.
  const defaultDateTime = useMemo(() => {
    const now = new Date();
    const next = new Date(now.getTime() + 60 * 60 * 1000);
    const m = next.getMinutes();
    next.setMinutes(m + ((15 - (m % 15)) % 15), 0, 0);
    return next;
  }, []);

  const seed = (() => {
    if (initialDate instanceof Date && isValidDate(initialDate)) return initialDate;
    if (typeof initialDate === "string" && initialDate) {
      const d = new Date(initialDate);
      if (isValidDate(d)) return d;
    }
    return defaultDateTime;
  })();

  const [pickedDate, setPickedDate] = useState(seed);
  const [hour12, setHour12] = useState(((seed.getHours() % 12) || 12).toString());
  const [minute, setMinute] = useState(
    MINUTE_OPTIONS.includes(seed.getMinutes().toString().padStart(2, "0"))
      ? seed.getMinutes().toString().padStart(2, "0")
      : "00"
  );
  const [meridiem, setMeridiem] = useState(seed.getHours() >= 12 ? "PM" : "AM");
  const [submitting, setSubmitting] = useState(false);

  // Reseed local state every time the dialog opens (so reopening after a
  // close shows the fresh "now + 1h" default instead of stale values).
  useEffect(() => {
    if (!open) return;
    setPickedDate(seed);
    setHour12(((seed.getHours() % 12) || 12).toString());
    const mm = seed.getMinutes().toString().padStart(2, "0");
    setMinute(MINUTE_OPTIONS.includes(mm) ? mm : "00");
    setMeridiem(seed.getHours() >= 12 ? "PM" : "AM");
    setSubmitting(false);
    // We intentionally only depend on `open` \u2014 reseeding while the user is
    // actively editing would lose their input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const combined = useMemo(() => {
    if (!pickedDate || !isValidDate(pickedDate)) return null;
    return applyTimeOfDay(pickedDate, hour12, minute, meridiem);
  }, [pickedDate, hour12, minute, meridiem]);

  const now = useMemo(() => new Date(), [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const isFuture = combined && isValidDate(combined) && combined.getTime() > now.getTime() + 30 * 1000;
  const isValidCombined = !!combined && isValidDate(combined);

  // Surfaceable inline error \u2014 single source of truth.
  let inlineError = null;
  if (!isValidCombined) {
    inlineError = "Choose a valid future date and time.";
  } else if (!isFuture) {
    inlineError = "Choose a future time.";
  }

  const canPublish = !!eligibility?.can_publish;
  const blockedByPlatform = !canPublish && !!eligibility && !eligLoading;

  const confirmDisabled =
    submitting ||
    externalLoading ||
    eligLoading ||
    !!inlineError ||
    blockedByPlatform;

  // Multi-platform quota pre-check (frontend convenience only — backend
  // still enforces via 402). Counts platforms that are individually
  // eligible; if that count exceeds the user's remaining monthly quota,
  // block the schedule button and show a clear banner.
  const { sub: billingSub } = useBillingSubscription();
  const eligibleForCount = isMulti
    ? normalizedTargets.filter((plat) => {
        const e = eligByPlatform[plat]?.eligibility;
        return !!(e && e.can_publish);
      })
    : (canPublish ? [platform] : []);
  const quotaEval = evaluatePublishQuota(billingSub, publishUnitsNeeded(eligibleForCount));
  const quotaBlocked = !!quotaEval.blocked;
  const finalConfirmDisabled = confirmDisabled || quotaBlocked;

  const handleConfirm = async () => {
    if (finalConfirmDisabled || !combined) return;
    // Final defensive check before calling toISOString \u2014 the crash bug
    // came from calling this on an invalid date.
    if (!isValidDate(combined)) return;
    setSubmitting(true);
    try {
      await onConfirm?.(combined.toISOString());
    } finally {
      setSubmitting(false);
    }
  };

  // Disable past dates in the calendar (today still allowed; the time
  // validation above handles the "today but past time" edge).
  const calendarDisabled = (d) => {
    if (!isValidDate(d)) return true;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return d < startOfToday;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] rounded-xl sm:max-w-md"
        data-testid={`${testidPrefix}-content`}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-lg" data-testid={`${testidPrefix}-title`}>
            Schedule post
          </DialogTitle>
          <DialogDescription className="text-[color:var(--text-2)]">
            Postmoro will publish this post at the selected date and time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Platform + connection status.
              Multi-platform mode: render one row per selected target with its
              own eligibility (so the user can see e.g. "Instagram skipped —
              requires Meta approval" alongside "X: ready to schedule").
              Single-platform mode: legacy singular block, unchanged.        */}
          {isMulti ? (
            <div
              className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2.5 space-y-1.5"
              data-testid={`${testidPrefix}-platform-status-multi`}
            >
              <div className="text-xs text-[color:var(--text-2)] font-semibold mb-1">
                Publishing to {normalizedTargets.length} platforms
              </div>
              {normalizedTargets.map((plat) => {
                const e = (eligByPlatform[plat] && eligByPlatform[plat].eligibility) || null;
                const loading = !!(eligByPlatform[plat] && eligByPlatform[plat].loading);
                const ok = !!(e && e.can_publish);
                return (
                  <div
                    key={plat}
                    className="flex items-start gap-2 text-xs"
                    data-testid={`${testidPrefix}-platform-row-${plat.toLowerCase()}`}
                  >
                    {loading ? (
                      <Loader2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[color:var(--text-3)] animate-spin" />
                    ) : ok ? (
                      <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[color:var(--success)]" />
                    ) : (
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[color:var(--accent-warm)]" />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-[color:var(--text-1)]">{plat}</span>
                      <span className="text-[color:var(--text-3)]">
                        {loading
                          ? " · checking…"
                          : ok
                            ? ` · ready${e && e.mode === "demo" ? " (demo)" : ""}`
                            : ` · ${e && e.message ? e.message : "Not ready"}`}
                      </span>
                    </div>
                    {!ok && e && e.cta ? (
                      <Link to={e.cta.href} className="shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] h-6 px-2 text-[11px]"
                          data-testid={`${testidPrefix}-platform-cta-${plat.toLowerCase()}`}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          {e.cta.label}
                        </Button>
                      </Link>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : platform ? (
            <div
              className={
                "rounded-md border px-3 py-2.5 flex items-start gap-2 text-xs " +
                (canPublish
                  ? "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
                  : "border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)]")
              }
              data-testid={`${testidPrefix}-platform-status`}
            >
              {canPublish ? (
                <CheckCircle2 className="h-4 w-4 text-[color:var(--success)] mt-0.5 shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 text-[color:var(--accent-warm)] mt-0.5 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[color:var(--text-2)]">
                  Publishing to{" "}
                  <span className="font-semibold text-[color:var(--text-1)]">{platform}</span>
                </div>
                {eligLoading ? (
                  <div className="text-[color:var(--text-3)] mt-1">Checking connection…</div>
                ) : canPublish ? (
                  <div className="text-[color:var(--text-3)] mt-1">
                    Connected{eligibility?.mode === "demo" ? " (demo)" : ""} · ready to schedule
                  </div>
                ) : (
                  <div className="text-[color:var(--text-2)] mt-1 leading-relaxed">
                    {eligibility?.message || "This platform isn't ready for publishing yet."}
                  </div>
                )}
              </div>
              {!canPublish && eligibility?.cta ? (
                <Link to={eligibility.cta.href} className="shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] h-7 px-2"
                    data-testid={`${testidPrefix}-platform-cta`}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    {eligibility.cta.label}
                  </Button>
                </Link>
              ) : null}
            </div>
          ) : null}

          {/* Date picker */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
              Date
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start text-left border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)] hover:text-[color:var(--text-1)]"
                  data-testid={`${testidPrefix}-date-trigger`}
                >
                  <CalendarIcon className="h-4 w-4 mr-2 text-[color:var(--text-3)]" />
                  {pickedDate && isValidDate(pickedDate)
                    ? format(pickedDate, "EEE, MMM d, yyyy")
                    : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-auto p-0 bg-[color:var(--surface-1)] border-[color:var(--border-subtle)]"
              >
                <Calendar
                  mode="single"
                  selected={pickedDate}
                  onSelect={(d) => {
                    if (d && isValidDate(d)) setPickedDate(d);
                  }}
                  disabled={calendarDisabled}
                  initialFocus
                  data-testid={`${testidPrefix}-calendar`}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time picker (12-hour AM/PM) */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
              Time
            </label>
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-2">
              <div className="relative">
                <Clock className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-3)] pointer-events-none" />
                <Select value={hour12} onValueChange={setHour12}>
                  <SelectTrigger
                    className="pl-7 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                    data-testid={`${testidPrefix}-hour`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOUR_OPTIONS.map((h) => (
                      <SelectItem key={h} value={h.toString()}>
                        {h.toString().padStart(2, "0")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger
                  className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                  data-testid={`${testidPrefix}-minute`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MINUTE_OPTIONS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={meridiem} onValueChange={setMeridiem}>
                <SelectTrigger
                  className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                  data-testid={`${testidPrefix}-meridiem`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MERIDIEM_OPTIONS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-[color:var(--text-3)] mt-1" data-testid={`${testidPrefix}-timezone`}>
              <Globe className="h-3 w-3" />
              <span>Timezone: {tz}</span>
              {isValidCombined ? (
                <span className="ml-auto font-mono text-[color:var(--text-2)]">
                  {format(combined, "EEE, MMM d \u2022 h:mm a")}
                </span>
              ) : null}
            </div>
          </div>

          {/* Inline validation error */}
          {inlineError ? (
            <div
              className="rounded-md border border-[color:rgba(255,92,108,0.35)] bg-[color:rgba(255,92,108,0.06)] px-3 py-2 text-xs text-[color:var(--danger)] flex items-start gap-2"
              data-testid={`${testidPrefix}-inline-error`}
            >
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span className="leading-relaxed">{inlineError}</span>
            </div>
          ) : null}
          <QuotaBlockBanner
            hidden={!quotaBlocked}
            tone="danger"
            title="Monthly post limit"
            message={`You have ${quotaEval.remaining} post${quotaEval.remaining === 1 ? "" : "s"} remaining this month. Scheduling to ${eligibleForCount.join(" + ")} would use ${quotaEval.needed} post${quotaEval.needed === 1 ? "" : "s"}. Remove one platform or upgrade to continue.`}
            testid={`${testidPrefix}-quota-block`}
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting || externalLoading}
            data-testid={`${testidPrefix}-cancel`}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={finalConfirmDisabled}
            title={blockedByPlatform ? eligibility?.message || undefined : undefined}
            className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`${testidPrefix}-confirm`}
          >
            {submitting || externalLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CalendarPlus className="h-4 w-4 mr-2" />
            )}
            Schedule post
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
